import {
  Plane,
  SquareMenu,
  Brush,
  GraduationCap,
  CheckCircle2,
  CircleX,
  Video,
} from 'lucide-react';
import { Link } from 'react-router';

import { getSessionLabel } from '@/utils';

import { HOBBY_CLASS_IMAGES } from '../../constants';
import { Schedule } from '../../types';

const SESSION_LABEL = getSessionLabel();

export const getColorsFromVariant = (
  variant?: string
): {
  icon: string;
  bg: string;
  text: string;
} => {
  if (variant) {
    switch (variant) {
      // Parent Connect reuses the same blue palette as English.
      case 'english':
      case 'parent_connect': {
        return {
          icon: 'text-blue-chaos',
          bg: 'bg-blue-chaos-light-cardbg',
          text: 'text-blue-chaos-dark',
        };
      }
      case 'hobby': {
        return {
          icon: 'text-hot-sun-yellow',
          bg: 'bg-hot-sun-yellow-light-cardbg',
          text: 'text-hot-sun-dark',
        };
      }
      case 'master_class': {
        return {
          icon: 'text-underwater-fern-green',
          bg: 'bg-underwater-fern-green-light-cardbg',
          text: 'text-underwater-fern-green-dark',
        };
      }
      case 'math': {
        return {
          icon: 'text-cold-lips',
          bg: 'bg-cold-lips-light-cardbg',
          text: 'text-cold-lips-dark',
        };
      }
    }
  }

  return {
    icon: 'text-cold-lips',
    bg: 'bg-cold-lips-light-cardbg',
    text: 'text-cold-lips-dark',
  };
};

// Parent Connect / PTM rows have their own pill copy + colors (and the
// trainer-rescheduled state is a link to the respond page). Driven by the
// derived `pcState`, NOT the shared `status` buckets, so "Attended" etc.
// never leak onto a trainer-parent meeting.
export const getParentConnectStatusContent = (
  pcState?: Schedule.ParentConnectState,
  respondHref?: string
) => {
  switch (pcState) {
    case 'request_sent': {
      return (
        <span className="flex items-center text-magical-moonlight-d2">
          Request sent
        </span>
      );
    }
    case 'request_pending': {
      // Trainer suggested a new time -> parent must respond. Link to the
      // respond page when we have a variant_id; otherwise plain text so
      // the pill never becomes a dead link.
      if (respondHref) {
        return (
          <Link
            to={respondHref}
            className="flex items-center font-semibold text-blue-chaos underline underline-offset-2 hover:opacity-80"
          >
            Request pending
          </Link>
        );
      }
      return (
        <span className="flex items-center text-blue-chaos">
          Request pending
        </span>
      );
    }
    case 'accepted': {
      return (
        <span className="flex items-center text-underwater-fern-green">
          <CheckCircle2 className="w-4 h-4 mr-2" />
          Accepted
        </span>
      );
    }
    case 'awaiting_acknowledgement': {
      return <span className="text-magical-moonlight-d2">Booked</span>;
    }
    case 'acknowledged': {
      return (
        <span className="flex items-center text-underwater-fern-green">
          <CheckCircle2 className="w-4 h-4 mr-2" />
          Acknowledged
        </span>
      );
    }
    case 'completed': {
      return (
        <span className="flex items-center text-underwater-fern-green">
          <CheckCircle2 className="w-4 h-4 mr-2" />
          Completed
        </span>
      );
    }
    case 'cancelled': {
      return (
        <span className="flex items-center text-bhanzu-orange-dark">
          <CircleX className="w-4 h-4 mr-2" />
          Cancelled
        </span>
      );
    }
    case 'removed': {
      return (
        <span className="flex items-center text-fusion-red-dark">
          <CircleX className="w-4 h-4 mr-2" />
          Deleted
        </span>
      );
    }
  }
  return '';
};

export const getStatusContent = (status?: Schedule.Lesson['status']) => {
  switch (status) {
    case 'upcoming': {
      return `Upcoming ${SESSION_LABEL.capitalize}`;
    }
    case 'holiday': {
      return 'Holiday';
    }
    case 'missed': {
      return (
        <span className="flex items-center text-fusion-red-dark">
          <CircleX className="w-4 h-4 mr-2" />
          Missed
        </span>
      );
    }
    case 'cancelled': {
      return (
        <span className="flex items-center text-bhanzu-orange-dark">
          <CircleX className="w-4 h-4 mr-2" />
          Cancelled
        </span>
      );
    }
    case 'attended': {
      return (
        <span className="flex items-center text-underwater-fern-green">
          <CheckCircle2 className="w-4 h-4 mr-2" />
          Attended
        </span>
      );
    }
  }
  return '';
};

// Helper to create icon wrapper with consistent styling
const IconWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-white box-border flex gap-2.5 items-center justify-start p-2 rounded-xl shrink-0">
    {children}
  </div>
);

// Helper to get icon size based on mobile state
const getIconSize = (isMobile?: boolean) => (isMobile ? 20 : 24);

// Helper to get status color
const getStatusColor = (status?: Schedule.Lesson['status']) => {
  return status &&
    ['holiday', 'missed', 'attended', 'cancelled'].includes(status)
    ? 'text-magical-moonlight-d2'
    : '';
};

// Helper to render hobby class icon
const renderHobbyClassIcon = (
  title?: string,
  icon?: string,
  color?: string,
  isMobile?: boolean
) => {
  const hobbyImage = title ? HOBBY_CLASS_IMAGES[title] : null;

  if (hobbyImage) {
    return (
      <img
        src={hobbyImage.image}
        alt={hobbyImage.alt}
        className="w-9 h-9 scale-150 md:scale-190 -ml-2 md:-ml-0"
      />
    );
  }

  return (
    <IconWrapper>
      <Brush size={getIconSize(isMobile)} className={`${icon} ${color}`} />
    </IconWrapper>
  );
};

export const getIcon = (
  variant?: Schedule.Lesson['variant'],
  status?: Schedule.Lesson['status'],
  title?: string,
  isMobile?: boolean
) => {
  const { icon } = getColorsFromVariant(variant ?? 'default');
  const color = getStatusColor(status);

  // Show plane icon for holidays regardless of variant
  if (status === 'holiday') {
    return (
      <IconWrapper>
        <Plane
          size={getIconSize(isMobile)}
          className={`text-magical-moonlight-d2 ${color}`}
        />
      </IconWrapper>
    );
  }

  switch (variant) {
    case 'math':
    case 'english': {
      return (
        <IconWrapper>
          <SquareMenu
            size={getIconSize(isMobile)}
            className={`${icon} ${color}`}
          />
        </IconWrapper>
      );
    }
    case 'hobby_class': {
      return renderHobbyClassIcon(title, icon, color, isMobile);
    }
    case 'parent_connect': {
      return (
        <IconWrapper>
          <Video size={getIconSize(isMobile)} className={`${icon} ${color}`} />
        </IconWrapper>
      );
    }
    case 'master_class': {
      return (
        <IconWrapper>
          <GraduationCap
            size={getIconSize(isMobile)}
            className={`${icon} ${color}`}
          />
        </IconWrapper>
      );
    }
    default: {
      return <SquareMenu size={24} className={`${icon} ${color}`} />;
    }
  }
};
